import React, { useState, useContext, useEffect, useRef } from "react";
import Select from "react-select";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import "./EmployeeTimesheet.css";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import {
  getAllGroups,
  getUsersSummary,
  getUserById,
  getEmployeeReportHours,
  getEmployeeAttendanceMonth,
  getEmployeeAttendance6Month,
} from "../../../../api/api";

registerLocale("ru", ru);

// Компонент для отображения отчета
const ReportContent = ({
  employeeName,
  period,
  reportData,
  calculatePieChartData,
  createArcPath,
}) => {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  const formatHours = (value) => value || 0;

  const totalForChart =
    reportData.timesheetChartTotal ?? reportData.timesheet ?? 0;

  const pieChartSegments = calculatePieChartData(
    reportData.categories,
    totalForChart
  );

  const handleSegmentMouseEnter = (event, segment) => {
    const container = event.currentTarget.closest(".pie-chart-container");
    if (container) {
      const rect = container.getBoundingClientRect();
      setTooltipPosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    }
    setHoveredSegment(segment);
  };

  const handleSegmentMouseLeave = () => {
    setHoveredSegment(null);
  };

  const centerX = 180;
  const centerY = 180;
  const outerRadius = 150;
  const innerRadius = 75;

  return (
    <div className="report-content">
      {/* Карточки метрик */}
      <div className="metrics-cards">
        <div className="metric-card metric-card--norm">
          <div className="metric-card-icon">🕐</div>
          <div className="metric-card-content">
            <div className="metric-card-label">Норма в месяц</div>
            <div className="metric-card-value">
              {formatHours(reportData.normMonth)} ч.
            </div>
          </div>
        </div>
        <div className="metric-card metric-card--norm">
          <div className="metric-card-icon">🕐</div>
          <div className="metric-card-content">
            <div className="metric-card-label">Норма по сотруднику</div>
            <div className="metric-card-value">
              {formatHours(reportData.normEmployee)} ч.
            </div>
          </div>
        </div>
        <div className="metric-card metric-card--fact">
          <div className="metric-card-icon">✓</div>
          <div className="metric-card-content">
            <div className="metric-card-label">Факт</div>
            <div className="metric-card-value">
              {formatHours(reportData.fact)} ч.
            </div>
          </div>
        </div>
        <div className="metric-card metric-card--timesheet">
          <div className="metric-card-icon">📄</div>
          <div className="metric-card-content">
            <div className="metric-card-label">По табелю</div>
            <div className="metric-card-value">
              {formatHours(reportData.timesheet)} ч.
            </div>
          </div>
        </div>
        <div className="metric-card metric-card--deviation">
          <div className="metric-card-icon">⚠</div>
          <div className="metric-card-content">
            <div className="metric-card-label">Отклонение</div>
            <div
              className={`metric-card-value ${
                reportData.deviation < 0 ? "negative" : ""
              }`}
            >
              {formatHours(reportData.deviation) > 0 ? "+" : ""}
              {formatHours(reportData.deviation)} ч.
            </div>
          </div>
        </div>
      </div>

      {/* Диаграмма и легенда */}
      <div className="chart-and-legend">
        {/* Круговая диаграмма */}
        <div className="pie-chart-container">
          <svg
            ref={svgRef}
            width="360"
            height="360"
            viewBox="0 0 360 360"
            className="pie-chart"
          >
            {pieChartSegments.map((segment, index) => (
              <path
                key={index}
                d={createArcPath(
                  segment.startAngle,
                  segment.endAngle,
                  innerRadius,
                  outerRadius
                )}
                fill={segment.color}
                stroke="#fff"
                strokeWidth="2"
                transform={`translate(${centerX}, ${centerY})`}
                onMouseEnter={(e) => handleSegmentMouseEnter(e, segment)}
                onMouseLeave={handleSegmentMouseLeave}
                style={{ cursor: "pointer" }}
                opacity={
                  hoveredSegment === segment ? 1 : hoveredSegment ? 0.5 : 1
                }
              />
            ))}
            {/* Центральный круг с общим значением */}
            <circle
              cx={centerX}
              cy={centerY}
              r={innerRadius}
              fill="white"
              stroke="#E0E0E0"
              strokeWidth="2"
            />
            <text
              x={centerX}
              y={centerY - 12}
              textAnchor="middle"
              className="pie-chart-center-value"
            >
              {formatHours(totalForChart)} ч.
            </text>
            <text
              x={centerX}
              y={centerY + 12}
              textAnchor="middle"
              className="pie-chart-center-label"
            >
              Всего
            </text>
          </svg>

          {/* Тултип */}
          {hoveredSegment && (
            <div
              className="pie-chart-tooltip"
              style={{
                left: `${tooltipPosition.x + 20}px`,
                top: `${tooltipPosition.y - 20}px`,
              }}
            >
              <div className="tooltip-category">{hoveredSegment.name}</div>
              <div className="tooltip-value">
                {formatHours(hoveredSegment.hours)} ч.
              </div>
              {hoveredSegment.dates && (
                <div className="tooltip-dates">{hoveredSegment.dates}</div>
              )}
            </div>
          )}
        </div>

        {/* Легенда */}
        <div className="employee-timesheet-legend-container">
          <div className="employee-timesheet-legend-title">Итоги за период</div>
          <div className="employee-timesheet-legend-items">
            {reportData.categories.map((category, index) => (
              <div key={index} className="employee-timesheet-legend-item">
                <div
                  className="employee-timesheet-legend-dot"
                  style={{ backgroundColor: category.color }}
                />
                <div className="employee-timesheet-legend-content">
                  <div className="employee-timesheet-legend-row">
                    <span className="employee-timesheet-legend-name">
                      {category.name}
                    </span>
                    <span className="employee-timesheet-legend-hours">
                      {formatHours(category.hours)} ч.
                    </span>
                    {category.dates && (
                      <span className="employee-timesheet-legend-dates">
                        {category.dates}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Кастомные стили для селектов
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    padding: "5px",
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 300,
    lineHeight: "19.12px",
    minHeight: "42px",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "white",
    "&:hover": {
      borderColor: "#E0E0E0",
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    borderRadius: "10px",
    marginTop: "5px",
    width: "100%",
    zIndex: 2,
    backgroundColor: "white",
  }),
  option: (provided, state) => ({
    ...provided,
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 300,
    backgroundColor: state.isSelected ? "#f5f5f5" : "white",
    color: state.isSelected ? "#333" : "#333",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#999",
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 300,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#333",
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 300,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 8px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#666",
  }),
};

const EmployeeTimesheet = () => {
  const { userData } = useContext(UserContext);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [departmentEmployees, setDepartmentEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState("month"); // "month" или "custom"
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [employeeDetailsLoading, setEmployeeDetailsLoading] = useState(false);
  const [employeeDetailsError, setEmployeeDetailsError] = useState(null);

  // Загрузка отделов при монтировании
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setLoading(true);
        const departmentsData = await getAllGroups();

        const departmentsList = departmentsData
          .filter((dept) => dept.is_active !== false)
          .map((dept) => ({
            value: dept.group_id || dept.id,
            label: dept.name,
          }));

        setDepartments(departmentsList);
      } catch (error) {
        console.error("Ошибка при загрузке отделов:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDepartments();
  }, []);

  // Загрузка всех сотрудников для возможности выбора сотрудника первым
  useEffect(() => {
    const loadAllEmployees = async () => {
      try {
        const employeesData = await getUsersSummary({
          isActive: true,
          pageSize: 1000,
        });

        const employeesList = (employeesData.users || []).map((user) => {
          // Пытаемся получить ID отдела из разных источников
          let departmentId = null;
          if (user.group_id) {
            departmentId = user.group_id;
          } else if (user.group && user.group.id) {
            departmentId = user.group.id;
          } else if (user.groups && user.groups.length > 0) {
            departmentId = user.groups[0].group_id || user.groups[0].id;
          }

          return {
            value: user.user_id || user.id,
            label: `${user.surname || ""} ${user.name || ""}`.trim(),
            user: user,
            departmentId: departmentId,
          };
        });

        setAllEmployees(employeesList);
      } catch (error) {
        console.error("Ошибка при загрузке всех сотрудников:", error);
      }
    };

    loadAllEmployees();
  }, []);

  // Загрузка сотрудников отдела при выборе отдела
  useEffect(() => {
    const loadDepartmentEmployees = async () => {
      if (!selectedDepartment?.value) {
        setDepartmentEmployees([]);
        return;
      }

      try {
        const employeesData = await getUsersSummary({
          groupId: selectedDepartment.value,
          isActive: true,
          pageSize: 1000,
        });

        const employeesList = (employeesData.users || []).map((user) => ({
          value: user.user_id || user.id,
          label: `${user.surname || ""} ${user.name || ""}`.trim(),
          user: user,
        }));

        setDepartmentEmployees(employeesList);
      } catch (error) {
        console.error("Ошибка при загрузке сотрудников отдела:", error);
        setDepartmentEmployees([]);
      }
    };

    loadDepartmentEmployees();
  }, [selectedDepartment]);

  // Обработчик выбора отдела
  const handleDepartmentChange = (option) => {
    setSelectedDepartment(option);
    // Сбрасываем выбранного сотрудника при смене отдела
    setSelectedEmployee(null);
  };

  // Обработчик выбора сотрудника
  const handleEmployeeChange = async (option) => {
    setSelectedEmployee(option);

    // Если сотрудник выбран первым, находим его отдел
    if (option && !selectedDepartment) {
      // Сначала проверяем сохраненный departmentId из allEmployees
      if (option.departmentId) {
        const department = departments.find(
          (dept) => dept.value === option.departmentId
        );
        if (department) {
          setSelectedDepartment(department);
          return;
        }
      }

      // Если не нашли в сохраненных данных, загружаем полные данные пользователя
      try {
        const userResponse = await getUserById(option.value);
        const user = userResponse.users?.[0] || userResponse;

        // Ищем отдел сотрудника из разных возможных источников
        let departmentId = null;

        // Проверяем groups массив
        if (user.groups && user.groups.length > 0) {
          const userGroup = user.groups[0];
          departmentId = userGroup.group_id || userGroup.id;
        }
        // Проверяем group_id напрямую
        else if (user.group_id) {
          departmentId = user.group_id;
        }
        // Проверяем latest_group из истории
        else if (user.latest_group) {
          departmentId = user.latest_group.group_id || user.latest_group.id;
        }

        if (departmentId) {
          const department = departments.find(
            (dept) => dept.value === departmentId
          );

          if (department) {
            setSelectedDepartment(department);
          }
        }
      } catch (error) {
        console.error("Ошибка при получении данных сотрудника:", error);
      }
    }
  };

  // Получение списка сотрудников для отображения в селекте
  const getEmployeesForSelect = () => {
    if (selectedDepartment) {
      return departmentEmployees;
    }
    return allEmployees;
  };

  // Форматирование месяца для отображения
  const formatMonth = (date) => {
    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Проверка, можно ли загрузить отчет
  const canLoadReport = () => {
    if (!selectedEmployee) return false;
    if (periodType === "month") {
      return selectedMonth !== null;
    } else {
      return dateRange.startDate !== null && dateRange.endDate !== null;
    }
  };

  // Преобразование ответа API в структуру данных для отчета
  const transformReportData = (apiData) => {
    if (!apiData || !apiData.months || apiData.months.length === 0) {
      return null;
    }

    const totals = apiData.months.reduce(
      (acc, month) => ({
        expected_hours: acc.expected_hours + (month.expected_hours || 0),
        expected_hours_adjusted:
          acc.expected_hours_adjusted + (month.expected_hours_adjusted || 0),
        hours_in_office: acc.hours_in_office + (month.hours_in_office || 0),
        remote_hours: acc.remote_hours + (month.remote_hours || 0),
        vacation_hours: acc.vacation_hours + (month.vacation_hours || 0),
        overtime_hours: acc.overtime_hours + (month.overtime_hours || 0),
        business_trip_hours:
          acc.business_trip_hours + (month.business_trip_hours || 0),
        sick_hours: acc.sick_hours + (month.sick_hours || 0),
        pov_hours: acc.pov_hours + (month.pov_hours || 0),
        unpaid_vacation_hours:
          acc.unpaid_vacation_hours + (month.unpaid_vacation_hours || 0),
        actual_hours_for_month:
          acc.actual_hours_for_month + (month.actual_hours_for_month || 0),
        report_hours: acc.report_hours + (month.report_hours || 0),
      }),
      {
        expected_hours: 0,
        expected_hours_adjusted: 0,
        hours_in_office: 0,
        remote_hours: 0,
        vacation_hours: 0,
        overtime_hours: 0,
        business_trip_hours: 0,
        sick_hours: 0,
        pov_hours: 0,
        unpaid_vacation_hours: 0,
        actual_hours_for_month: 0,
        report_hours: 0,
      }
    );

    // hours_in_office уже включает в себя переработки и командировки,
    // поэтому вычитаем их из "обычных" офисных часов и показываем отдельными категориями
    const rawOfficeHours = totals.hours_in_office || 0;
    const remoteHours = totals.remote_hours || 0;
    const vacationHours = totals.vacation_hours || 0;
    const sickHours = totals.sick_hours || 0;
    const povHours = totals.pov_hours || 0;
    const unpaidVacationHours = totals.unpaid_vacation_hours || 0;
    const overtimeHours = totals.overtime_hours || 0;
    const businessTripHours = totals.business_trip_hours || 0;
    const officeHours = Math.max(
      rawOfficeHours - overtimeHours - businessTripHours,
      0
    );

    // Норма в месяц: полная норма по табелю (expected_hours)
    const normMonth = totals.expected_hours || 0;

    // Норма по сотруднику: норма с учётом отпусков/больничных (expected_hours_adjusted)
    const normEmployee =
      totals.expected_hours_adjusted || totals.expected_hours || 0;

    // Факт: фактически отработанные часы за период (actual_hours_for_month)
    const fact = totals.actual_hours_for_month || 0;

    // По табелю: report_hours
    const timesheet = totals.report_hours || 0;

    // Общее количество часов для пончика (включая отпуск и больничный)
    const timesheetChartTotal =
      officeHours +
      remoteHours +
      vacationHours +
      sickHours +
      povHours +
      unpaidVacationHours +
      overtimeHours +
      businessTripHours;

    const deviation = fact - timesheet;

    const categories = [
      {
        name: "Офис",
        hours: officeHours,
        color: "#4CAF50",
        dates: null,
      },
      {
        name: "Удалённая работа",
        hours: remoteHours,
        color: "#9C27B0",
        dates: null,
      },
      {
        name: "Отпуск",
        hours: vacationHours,
        color: "#2196F3",
        dates: null,
      },
      {
        name: "Больничный",
        hours: sickHours,
        color: "#F44336",
        dates: null,
      },
      {
        name: "Отгулы",
        hours: povHours,
        color: "#03A9F4",
        dates: null,
      },
      {
        name: "Отпуск без содержания",
        hours: unpaidVacationHours,
        color: "#607D8B",
        dates: null,
      },
      {
        name: "Переработка",
        hours: overtimeHours,
        color: "#FF9800",
        dates: null,
      },
      {
        name: "Командировка",
        hours: businessTripHours,
        color: "#795548",
        dates: null,
      },
    ];

    return {
      normMonth,
      normEmployee,
      fact,
      timesheet,
      timesheetChartTotal,
      deviation,
      categories,
    };
  };

  // Расчет углов для круговой диаграммы
  const calculatePieChartData = (categories, total) => {
    let currentAngle = -90; // Начинаем с верха
    const segments = [];

    // Фильтруем категории с часами > 0 и сортируем по убыванию для лучшего отображения
    const activeCategories = categories
      .filter((cat) => cat.hours > 0)
      .sort((a, b) => b.hours - a.hours);

    activeCategories.forEach((category) => {
      const angle = (category.hours / total) * 360;

      segments.push({
        ...category,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        percentage: (category.hours / total) * 100,
      });

      currentAngle += angle;
    });

    return segments;
  };

  // Функция для создания пути сегмента круга
  const createArcPath = (startAngle, endAngle, innerRadius, outerRadius) => {
    const start = polarToCartesian(0, 0, outerRadius, startAngle);
    const end = polarToCartesian(0, 0, outerRadius, endAngle);
    const innerStart = polarToCartesian(0, 0, innerRadius, startAngle);
    const innerEnd = polarToCartesian(0, 0, innerRadius, endAngle);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return [
      "M",
      start.x,
      start.y,
      "A",
      outerRadius,
      outerRadius,
      0,
      largeArcFlag,
      1,
      end.x,
      end.y,
      "L",
      innerEnd.x,
      innerEnd.y,
      "A",
      innerRadius,
      innerRadius,
      0,
      largeArcFlag,
      0,
      innerStart.x,
      innerStart.y,
      "Z",
    ].join(" ");
  };

  // Преобразование полярных координат в декартовы
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  // Форматирование даты в YYYY-MM-DD
  const formatDateForApi = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Загрузка отчета при изменении сотрудника или периода
  useEffect(() => {
    const loadReport = async () => {
      if (!canLoadReport()) {
        setReportData(null);
        return;
      }

      if (!selectedEmployee || !selectedEmployee.value) {
        setReportData(null);
        return;
      }

      try {
        setReportLoading(true);
        setReportError(null);

        const employeeId = selectedEmployee.value;

        let params = { employeeId };

        if (periodType === "month" && selectedMonth) {
          params.year = selectedMonth.getFullYear();
          params.month = selectedMonth.getMonth() + 1;
        } else if (
          periodType === "custom" &&
          dateRange.startDate &&
          dateRange.endDate
        ) {
          params.dateFrom = formatDateForApi(dateRange.startDate);
          params.dateTo = formatDateForApi(dateRange.endDate);
        } else {
          setReportData(null);
          return;
        }

        const apiData = await getEmployeeReportHours(params);
        const transformed = transformReportData(apiData);

        if (!transformed) {
          setReportData({
            norm: 0,
            fact: 0,
            timesheet: 0,
            deviation: 0,
            categories: [],
          });
        } else {
          setReportData(transformed);
        }
      } catch (error) {
        console.error("Ошибка при загрузке отчета по часам сотрудника:", error);
        setReportError(
          error.message || "Ошибка при загрузке отчета по часам сотрудника"
        );
        setReportData(null);
      } finally {
        setReportLoading(false);
      }
    };

    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, periodType, selectedMonth, dateRange]);

  // Функция для загрузки детальных данных сотрудника
  const loadEmployeeDetails = async (
    employeeId,
    periodType,
    selectedMonth,
    dateRange
  ) => {
    if (!employeeId) {
      setEmployeeDetails(null);
      return;
    }

    try {
      setEmployeeDetailsLoading(true);
      setEmployeeDetailsError(null);

      let data;

      if (periodType === "month" && selectedMonth) {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth() + 1;
        data = await getEmployeeAttendanceMonth(employeeId, year, month);
      } else if (
        periodType === "custom" &&
        dateRange.startDate &&
        dateRange.endDate
      ) {
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);

        const startYear = startDate.getFullYear();
        const startMonth = startDate.getMonth() + 1;
        const endYear = endDate.getFullYear();
        const endMonth = endDate.getMonth() + 1;

        // Если период в пределах одного месяца, используем month
        if (startYear === endYear && startMonth === endMonth) {
          data = await getEmployeeAttendanceMonth(
            employeeId,
            startYear,
            startMonth
          );
        } else {
          // Иначе используем 6month (он поддерживает любой период до 6 месяцев)
          data = await getEmployeeAttendance6Month(
            employeeId,
            startYear,
            startMonth,
            endYear,
            endMonth
          );
        }
      } else {
        setEmployeeDetails(null);
        return;
      }

      setEmployeeDetails(data);
    } catch (error) {
      console.error("Ошибка при загрузке детальных данных сотрудника:", error);
      setEmployeeDetailsError(error.message);
    } finally {
      setEmployeeDetailsLoading(false);
    }
  };

  // Загрузка детальных данных сотрудника при изменении параметров
  useEffect(() => {
    if (selectedEmployee && selectedEmployee.value) {
      loadEmployeeDetails(
        selectedEmployee.value,
        periodType,
        selectedMonth,
        dateRange
      );
    } else {
      setEmployeeDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, periodType, selectedMonth, dateRange]);

  // Функция для сравнения времени в формате "HH:MM"
  const compareTime = (time1, time2) => {
    if (!time1 || !time2) return null;
    const [h1, m1] = time1.split(":").map(Number);
    const [h2, m2] = time2.split(":").map(Number);
    const minutes1 = h1 * 60 + m1;
    const minutes2 = h2 * 60 + m2;
    if (minutes1 < minutes2) return -1; // time1 раньше time2
    if (minutes1 > minutes2) return 1; // time1 позже time2
    return 0; // равны
  };

  // Получить детали сотрудника
  const getEmployeeDetails = () => {
    if (!employeeDetails) {
      return [];
    }

    // Если есть days (детализация по дням) - показываем как месяц
    if (employeeDetails.days) {
      return employeeDetails.days.map((day, index) => {
        const isWeekend = !day.workday_flag;

        // Если выходной день, показываем данные если они есть, иначе "Выходной день"
        if (isWeekend) {
          return {
            id: index + 1,
            date: new Date(day.work_date).toLocaleDateString("ru-RU"),
            scheduledStart: day.work_start_time || "Выходной день",
            actualArrival: day.first_entry || "Выходной день",
            delayGroup: "-",
            arrivalCategory: "Вовремя",
            departureTime: day.last_exit || "Выходной день",
            actualTimeInOffice: day.office_time || "Выходной день",
            scheduledTimeInOffice: day.report_time_spent || "0:00",
            deviation: day.time_diff || "0:00",
            comment: day.comment || "Все в порядке",
            isWeekend: true,
          };
        }

        // Проверяем, пришел ли сотрудник вовремя или раньше
        const isOnTimeOrEarly =
          day.first_entry &&
          day.work_start_time &&
          compareTime(day.first_entry, day.work_start_time) <= 0;

        // Если пришел вовремя или раньше, или нет данных о времени прихода
        if (isOnTimeOrEarly || !day.late_hours_minutes) {
          return {
            id: index + 1,
            date: new Date(day.work_date).toLocaleDateString("ru-RU"),
            scheduledStart: day.work_start_time,
            actualArrival: day.first_entry,
            delayGroup: "-",
            arrivalCategory: day.late_type || "Вовремя",
            departureTime: day.last_exit,
            actualTimeInOffice: day.office_time,
            scheduledTimeInOffice: day.report_time_spent,
            deviation: day.time_diff,
            comment: day.comment || "Все в порядке",
            isWeekend: false,
          };
        }

        // Если есть опоздание, определяем категорию по времени опоздания
        const lateMinutes =
          parseInt(day.late_hours_minutes.split(":")[0]) * 60 +
          parseInt(day.late_hours_minutes.split(":")[1]);

        return {
          id: index + 1,
          date: new Date(day.work_date).toLocaleDateString("ru-RU"),
          scheduledStart: day.work_start_time,
          actualArrival: day.first_entry,
          delayGroup: lateMinutes <= 10 ? "0-10" : "10-30",
          arrivalCategory:
            day.late_type ||
            (lateMinutes <= 10 ? "Незначительное опоздание" : "Опаздание"),
          departureTime: day.last_exit,
          actualTimeInOffice: day.office_time,
          scheduledTimeInOffice: day.report_time_spent,
          deviation: day.time_diff,
          comment: day.comment || "Все в порядке",
          isWeekend: false,
        };
      });
    }
    // Если есть months (агрегированные данные) - показываем как custom период
    else if (employeeDetails.months) {
      return employeeDetails.months.map((month, index) => ({
        id: index + 1,
        date: month.month,
        scheduledStart: month.workdays_count,
        actualArrival: month.lateness_count,
        delayGroup: "0-10",
        arrivalCategory: `${month.average_lateness} мин`,
        departureTime: `${month.intime_percentage}%`,
        actualTimeInOffice: month.report_mismatches_count,
        scheduledTimeInOffice: month.workdays_count,
        deviation: month.lateness_count,
        comment: "Все в порядке",
        isWeekend: false,
      }));
    }

    return [];
  };

  return (
    <div className="employee-timesheet-container">
      <div className="employee-timesheet-section">
        <h2 className="section-title">
          Табель по сотруднику за выбранный период
        </h2>

        {/* Селекты выбора отдела и сотрудника */}
        <div className="timesheet-selectors">
          <div className="selector-group">
            <label className="selector-label">
              Выбор отдела<span className="required">*</span>
            </label>
            <Select
              value={selectedDepartment}
              onChange={handleDepartmentChange}
              options={departments}
              placeholder="Выберите отдел"
              isDisabled={loading}
              isLoading={loading}
              styles={customSelectStyles}
              className="department-select"
              menuPlacement="auto"
              menuPosition="fixed"
              noOptionsMessage={() => "Нет доступных отделов"}
            />
          </div>

          <div className="selector-group">
            <label className="selector-label">
              Выбор сотрудника<span className="required">*</span>
            </label>
            <Select
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              options={getEmployeesForSelect()}
              placeholder="Выберите сотрудника"
              isDisabled={
                loading || (!selectedDepartment && allEmployees.length === 0)
              }
              isLoading={loading}
              styles={customSelectStyles}
              className="employee-select"
              menuPlacement="auto"
              menuPosition="fixed"
              noOptionsMessage={() =>
                selectedDepartment
                  ? "Нет доступных сотрудников"
                  : "Сначала выберите отдел или сотрудника"
              }
            />
          </div>
        </div>

        {/* Выбор периода */}
        <div className="period-selection">
          <label className="selector-label">
            Выбор периода<span className="required">*</span>
          </label>
          <div className="period-type-buttons">
            <button
              className={`period-type-button ${
                periodType === "month" ? "active" : ""
              }`}
              onClick={() => setPeriodType("month")}
            >
              Месяц
            </button>
            <button
              className={`period-type-button ${
                periodType === "custom" ? "active" : ""
              }`}
              onClick={() => setPeriodType("custom")}
            >
              Произвольный период
            </button>
          </div>

          {periodType === "month" ? (
            <div className="month-picker-container">
              <DatePicker
                selected={selectedMonth}
                onChange={(date) => setSelectedMonth(date)}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                locale="ru"
                className="month-picker"
                calendarClassName="custom-calendar month-year-calendar"
                placeholderText="Выберите месяц"
              />
            </div>
          ) : (
            <div className="date-range-picker-container">
              <DatePicker
                selected={dateRange.startDate}
                onChange={(date) =>
                  setDateRange({ ...dateRange, startDate: date })
                }
                selectsStart
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Начало периода"
                locale="ru"
                calendarStartDay={1}
                className="date-picker"
                calendarClassName="custom-calendar"
              />
              <span className="date-range-separator">—</span>
              <DatePicker
                selected={dateRange.endDate}
                onChange={(date) =>
                  setDateRange({ ...dateRange, endDate: date })
                }
                selectsEnd
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                minDate={dateRange.startDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Конец периода"
                locale="ru"
                calendarStartDay={1}
                className="date-picker"
                calendarClassName="custom-calendar"
              />
            </div>
          )}
        </div>

        {/* Область отображения отчета */}
        <div className="report-area">
          {!canLoadReport() ? (
            <div className="report-placeholder">
              <div className="report-placeholder-icon">📄</div>
              <p className="report-placeholder-text">
                Выберите сотрудника и период, чтобы увидеть отчет
              </p>
            </div>
          ) : reportLoading ? (
            <div className="report-placeholder">
              <div className="report-placeholder-icon">⏳</div>
              <p className="report-placeholder-text">Загружаем отчет...</p>
            </div>
          ) : reportError ? (
            <div className="report-placeholder">
              <div className="report-placeholder-icon">⚠</div>
              <p className="report-placeholder-text">{reportError}</p>
            </div>
          ) : !reportData || reportData.categories.length === 0 ? (
            <div className="report-placeholder">
              <div className="report-placeholder-icon">ℹ</div>
              <p className="report-placeholder-text">
                Нет данных за выбранный период
              </p>
            </div>
          ) : (
            <ReportContent
              employeeName={selectedEmployee?.label}
              period={
                periodType === "month"
                  ? formatMonth(selectedMonth)
                  : `${dateRange.startDate?.toLocaleDateString(
                      "ru-RU"
                    )} - ${dateRange.endDate?.toLocaleDateString("ru-RU")}`
              }
              reportData={reportData}
              calculatePieChartData={calculatePieChartData}
              createArcPath={createArcPath}
            />
          )}
        </div>

        {/* Таблица деталей сотрудника */}
        {selectedEmployee && (
          <div className="employee-details-section">
            {employeeDetailsLoading ? (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <h3>Загрузка детальных данных сотрудника...</h3>
              </div>
            ) : employeeDetailsError ? (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <h3>Ошибка загрузки детальных данных</h3>
                <p>{employeeDetailsError}</p>
                <button
                  onClick={() => {
                    if (selectedEmployee && selectedEmployee.value) {
                      loadEmployeeDetails(
                        selectedEmployee.value,
                        periodType,
                        selectedMonth,
                        dateRange
                      );
                    }
                  }}
                  style={{ marginTop: "20px", padding: "10px 20px" }}
                >
                  Попробовать снова
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="discipline-table">
                  <thead>
                    <tr>
                      {employeeDetails && employeeDetails.days ? (
                        <>
                          <th>Дата</th>
                          <th>Начало дня по графику</th>
                          <th>Фактическое время прихода</th>
                          <th>Группа опозданий</th>
                          <th>Категория прихода</th>
                          <th>Время ухода</th>
                          <th>Фактическое время в офисе</th>
                          <th>Время в офисе по табелю</th>
                          <th>Отклонение по табелю</th>
                          <th>Комментарий</th>
                        </>
                      ) : (
                        <>
                          <th>Месяц</th>
                          <th>Рабочих дней</th>
                          <th>Всего опозданий</th>
                          <th>Среднее время опоздания</th>
                          <th>% без опозданий</th>
                          <th>Количество дней с несоответствием табелю</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {getEmployeeDetails().map((detail, index) => (
                      <tr
                        key={detail.id}
                        className={detail.isWeekend ? "weekend-row" : ""}
                      >
                        <td>{detail.date}</td>
                        <td>{detail.scheduledStart}</td>
                        <td>{detail.actualArrival}</td>
                        {employeeDetails && employeeDetails.days ? (
                          <>
                            <td>
                              {detail.delayGroup !== "-" && (
                                <div
                                  className={`delay-group delay-group--${
                                    detail.delayGroup === "0-10"
                                      ? "yellow"
                                      : "orange"
                                  }`}
                                >
                                  {detail.delayGroup}
                                </div>
                              )}
                              {detail.delayGroup === "-" && "-"}
                            </td>
                            <td>
                              {detail.delayGroup === "-"
                                ? "Вовремя"
                                : detail.delayGroup === "0-10"
                                ? "Незначительное опоздание"
                                : detail.delayGroup === "10-30"
                                ? "Опаздание"
                                : detail.arrivalCategory || "-"}
                            </td>
                            <td>{detail.departureTime}</td>
                            <td>{detail.actualTimeInOffice}</td>
                            <td>{detail.scheduledTimeInOffice}</td>
                            <td
                              className={
                                detail.deviation?.startsWith("-")
                                  ? "negative-difference"
                                  : ""
                              }
                            >
                              {detail.deviation}
                            </td>
                            <td>
                              <div className="comment-cell">
                                {detail.comment === "Все в порядке" && (
                                  <span className="status-icon status-icon--good">
                                    ✓
                                  </span>
                                )}
                                {detail.comment !== "Все в порядке" && (
                                  <span className="status-icon status-icon--warning">
                                    ⚠
                                  </span>
                                )}
                                {detail.comment}
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{detail.arrivalCategory}</td>
                            <td>{detail.departureTime}</td>
                            <td>{detail.actualTimeInOffice}</td>
                            <td>{detail.scheduledTimeInOffice}</td>
                            <td>{detail.deviation}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeTimesheet;
